import {
  hideFloatingTooltip,
  hideFloatingTooltipForAnchor,
  showFloatingTooltip,
} from "./floating_tooltip";

const COLLAPSED = "collapsed";
const TOOLTIP_TARGET_SELECTOR =
  "[data-sidebar-nav-title], [data-sidebar-collapsed-tooltip]";

const lastPointer = { x: null, y: null };
let activeTooltipTarget = null;
let initialized = false;

function rememberPointer(event) {
  lastPointer.x = event.clientX;
  lastPointer.y = event.clientY;
}

function sidebarElement() {
  return document.querySelector(".app-shell__sidebar .sidebar");
}

function isSidebarCollapsed() {
  const layout =
    document.documentElement.dataset.sidebarLayout ||
    document.querySelector(".app-shell")?.dataset.sidebarLayout ||
    window.__DEPOZEN_SIDEBAR_LAYOUT__;

  return layout === COLLAPSED;
}

function tooltipTargetFrom(node) {
  const sidebar = sidebarElement();
  if (!(node instanceof Element) || !sidebar) return null;

  const target = node.closest(TOOLTIP_TARGET_SELECTOR);
  if (!target || !sidebar.contains(target)) return null;

  // Bouton dont le menu est ouvert (menu des environnements) : l'infobulle, collée à sa
  // droite, masquerait le haut du menu.
  return target.getAttribute("aria-expanded") === "true" ? null : target;
}

function findTooltipTargetAtPoint(clientX, clientY) {
  if (clientX == null || clientY == null) return null;

  const sidebar = sidebarElement();
  if (!sidebar) return null;

  for (const element of document.elementsFromPoint(clientX, clientY)) {
    if (!(element instanceof Element) || !sidebar.contains(element)) continue;

    const target = tooltipTargetFrom(element);
    if (target) return target;
  }

  return null;
}

function showCollapsedTooltip(target) {
  if (!target?.isConnected) return;

  const text = target.dataset.sidebarNavTitle || target.dataset.sidebarCollapsedTooltip;
  if (!text) return;

  showFloatingTooltip(target, text, { placement: "right" });
}

function clearCollapsedTooltip() {
  activeTooltipTarget = null;
  hideFloatingTooltip({ force: true });
}

function setActiveTooltipTarget(target) {
  if (activeTooltipTarget && !activeTooltipTarget.isConnected) {
    activeTooltipTarget = null;
  }

  if (target && !target.isConnected) target = null;

  if (target === activeTooltipTarget) {
    if (target) showCollapsedTooltip(target);
    return;
  }

  if (activeTooltipTarget) hideFloatingTooltipForAnchor(activeTooltipTarget);

  activeTooltipTarget = target;

  if (target) {
    showCollapsedTooltip(target);
  } else {
    hideFloatingTooltip({ force: true });
  }
}

function syncTooltipFromPointer(clientX, clientY) {
  if (!isSidebarCollapsed()) {
    clearCollapsedTooltip();
    return;
  }

  setActiveTooltipTarget(findTooltipTargetAtPoint(clientX, clientY));
}

function refreshTooltipUnderPointer() {
  if (!isSidebarCollapsed()) {
    clearCollapsedTooltip();
    return;
  }

  syncTooltipFromPointer(lastPointer.x, lastPointer.y);
}

function scheduleRefresh() {
  const run = () => refreshTooltipUnderPointer();

  // Turbo body swap: sidebar may appear after the first animation frame.
  requestAnimationFrame(() => {
    run();
    requestAnimationFrame(run);
  });
  window.setTimeout(run, 0);
}

function handlePointerMove(event) {
  rememberPointer(event);

  if (!isSidebarCollapsed()) {
    if (activeTooltipTarget) clearCollapsedTooltip();
    return;
  }

  const sidebar = sidebarElement();
  if (!sidebar) return;

  const target = findTooltipTargetAtPoint(event.clientX, event.clientY);
  if (target) {
    setActiveTooltipTarget(target);
  } else if (activeTooltipTarget) {
    clearCollapsedTooltip();
  }
}

function handlePointerOver(event) {
  if (!isSidebarCollapsed()) return;

  const sidebar = sidebarElement();
  if (!(event.target instanceof Node) || !sidebar?.contains(event.target)) return;

  rememberPointer(event);
  syncTooltipFromPointer(event.clientX, event.clientY);
}

function handleFocusIn(event) {
  if (!isSidebarCollapsed()) return;

  const target = tooltipTargetFrom(event.target);
  if (!target) return;

  const focusable = target.matches("a, button, [tabindex]")
    ? target
    : target.querySelector("a, button, [tabindex]");
  if (focusable && !focusable.matches(":focus-visible")) return;

  setActiveTooltipTarget(target);
}

function handleFocusOut(event) {
  if (!isSidebarCollapsed()) return;

  const target = tooltipTargetFrom(event.target);
  if (!target || activeTooltipTarget !== target) return;

  const related = event.relatedTarget;
  if (related instanceof Node && target.contains(related)) return;

  clearCollapsedTooltip();
}

function handleScroll(event) {
  if (!activeTooltipTarget?.isConnected || !isSidebarCollapsed()) return;
  if (event.target?.closest?.(".sidebar, .app-shell__sidebar")) return;

  showCollapsedTooltip(activeTooltipTarget);
}

export function initializeSidebarCollapsedTooltips() {
  if (initialized) return;
  initialized = true;

  document.addEventListener("pointermove", handlePointerMove, { passive: true });
  document.addEventListener("pointerover", handlePointerOver);
  document.addEventListener("pointerdown", rememberPointer, { passive: true });
  document.addEventListener("focusin", handleFocusIn);
  document.addEventListener("focusout", handleFocusOut);
  document.addEventListener("scroll", handleScroll, true);

  document.addEventListener("turbo:before-cache", clearCollapsedTooltip);
  document.addEventListener("turbo:render", scheduleRefresh);
  document.addEventListener("turbo:load", scheduleRefresh);

  document.addEventListener("depozen:sidebar-layout-change", scheduleRefresh);
}

export function refreshSidebarCollapsedTooltips() {
  scheduleRefresh();
}

export function clearSidebarCollapsedTooltips() {
  clearCollapsedTooltip();
}
